/**
 * browser-loop — React provider.
 *
 * Mounted ONCE at app/(app)/layout.tsx. Lifecycle:
 *
 *   1. SSR/prerender → renders nothing, no side effects, no patch install
 *   2. Client mount → reads config, lazy-imports the heavy modules,
 *      installs every patch, starts the client
 *   3. Unmount → stops the client (rare — only on full app shutdown)
 *
 * Lazy-import pattern (borrowed from Council's boot.ts): we dynamic-
 * import the modules inside an effect so SSR / static-prerender passes
 * never touch them. The browser-loop modules use `window` /
 * `localStorage` / `crypto.getRandomValues` at module load time;
 * importing them at SSR would break Next.js's worker.
 *
 * Failure isolation: every step is try/catch. If telemetry init
 * throws, the host app stays up — closed-loop §V.1 ("substrate bugs
 * corrupt every other diagnostic").
 */

"use client";

import { useEffect } from "react";

let booted = false;

async function bootBrowserLoop(): Promise<void> {
  if (booted) return;
  if (typeof window === "undefined") return;

  try {
    const [{ initClient }, patches] = await Promise.all([
      import("./client"),
      Promise.all([
        import("./patches/console"),
        import("./patches/errors"),
        import("./patches/fetch"),
        import("./patches/nav"),
        import("./patches/lifecycle"),
        import("./patches/interaction"),
        import("./patches/perf"),
        import("./patches/sse"),
      ]),
    ]);

    const client = initClient();
    if (!client) {
      // No bearer + RUM disabled → silent off, no patches installed
      // (zero overhead for anonymous users).
      booted = true;
      return;
    }

    const [console_, errors, fetch_, nav, lifecycle, interaction, perf, sse] =
      patches;
    // Order matters slightly: console patch first so any failure later
    // gets logged through us; fetch BEFORE other listeners use it.
    console_.installConsolePatch();
    errors.installErrorHooks();
    fetch_.installFetchWrap();
    nav.installNavHooks();
    lifecycle.installLifecycleHooks();
    interaction.installInteractionHooks();
    perf.installPerfHooks();
    sse.installSseTap();

    client.start();
    booted = true;
  } catch (err) {
    // Boot failed — host app continues unimpaired. Log via the original
    // console (not the patched one, since the patch may not have run).
    console.error("[browser-loop] boot failed:", err);
  }
}

export function BrowserLoopProvider(): null {
  useEffect(() => {
    void bootBrowserLoop();
    // No cleanup — the client outlives any provider unmount in
    // practice. Unmounting happens only on full app teardown, where
    // the page is closing anyway.
  }, []);

  return null;
}
